import React from 'react';
import styles from './upc-faqs.module.scss';
import {UpcSupportPage} from './upc-support-page';
import { Faq } from './faqs';
import {ExternalHyperlink} from "@workday/canvas-kit-react/button";
import {Subtext} from "@workday/canvas-kit-react/text";
import {Trans, useTranslation} from "react-i18next";
import { infoIcon } from '@workday/canvas-system-icons-web';
import { SystemIcon } from '@workday/canvas-kit-react/icon';
import colors from '@workday/canvas-colors-web';

function createFaq(faq: Faq, index: number) {
    return (
        <div key={index}>
            <h2>{faq.header}</h2>
            {faq.details.map(({id, title, description}) => (
                <details id={id} key={id}>
                    <summary>{title}</summary>
                    {description}
                </details>
            ))}
        </div>
    );
}

export function UpcFaqs() {
    const { t } = useTranslation('faq');
    const faqs: Faq[] = [
        {
            header: `${t('CreateAccount.Header')}`,
            details: [
                {
                    id: `how_do_I_create_an_account`,
                    title: t('CreateAccount.HowToCreate.Title'),
                    description: (
                        <>
                            <p>
                                {t('CreateAccount.HowToCreate.Disclaimer')}
                            </p>
                            <ol>
                                <li>
                                    <Trans i18nKey={'faq:CreateAccount.HowToCreate.StepOne'}>
                                        Click
                                        <strong> Accept Invite </strong>
                                        in the email.
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:CreateAccount.HowToCreate.StepTwo'}>
                                        Click
                                        <strong> Create Account (Invite only) </strong>
                                        on the Workday Central Login page.
                                    </Trans>
                                    <ol>
                                        <li>
                                            <Trans i18nKey={'faq:CreateAccount.HowToCreate.StepTwo.A'}>
                                                If you have lost the invitation email from Workday, you can follow this
                                                <ExternalHyperlink href='https://identity.workday.com/auth/login'> link </ExternalHyperlink>
                                                to create an account.
                                            </Trans>
                                        </li>
                                        <li>
                                            <Trans i18nKey={'faq:CreateAccount.HowToCreate.StepTwo.B'}>
                                                Click
                                                <strong> Create Account (Invite only) </strong>
                                                .
                                            </Trans>
                                        </li>
                                        <li>
                                            {t('CreateAccount.HowToCreate.StepTwo.C')}
                                        </li>
                                        <li>
                                            {t('CreateAccount.HowToCreate.StepTwo.D')}
                                        </li>
                                    </ol>
                                </li>
                            </ol>
                        </>
                    )
                },
                {
                    title: `${t('CreateAccount.WhyPhoneNeeded.Title')}`,
                    description: (
                        <p>
                            {t('CreateAccount.WhyPhoneNeeded.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('CreateAccount.WhatIsAuthenticator.Title')}`,
                    description: (
                        <p>
                            {t('CreateAccount.WhatIsAuthenticator.Content')}
                        </p>
                    )
                },
                {
                    id: 'how_do_I_set_up_an_authenticator_app',
                    title: `${t('CreateAccount.SetUpAuthenticator.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('faq:CreateAccount.SetUpAuthenticator.PartOne')}
                            </p>
                            <ul>
                                <li>{t('CreateAccount.SetUpAuthenticator.Google')}</li>
                                <li>{t('CreateAccount.SetUpAuthenticator.Duo')}</li>
                                <li>{t('CreateAccount.SetUpAuthenticator.Authy')}</li>
                                <li>{t('CreateAccount.SetUpAuthenticator.Microsoft')}</li>
                            </ul>
                            <p>
                                {t('faq:CreateAccount.SetUpAuthenticator.PartTwo')}
                            </p>
                        </>
                    )
                },
                {
                    title: `${t('CreateAccount.NoMobileDevice.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('CreateAccount.NoMobileDevice.PartOne')}
                                {t('CreateAccount.NoMobileDevice.PartTwo')}
                            </p>
                            <ul>
                                <li>{t('CreateAccount.NoMobileDevice.ExampleOne')}</li>
                                <li>{t('CreateAccount.NoMobileDevice.ExampleTwo')}</li>
                            </ul>
                        </>
                    )
                }
            ]
        },
        {
            header: `${t('RecoveringAccount.Title')}`,
            details: [
                {
                    title: `${t('RecoveringAccount.WhatEmail.Title')}`,
                    description: <p>{t('RecoveringAccount.WhatEmail.Content')}</p>
                },
                {
                    title: `${t('RecoveringAccount.LostEmailAccess.Title')}`,
                    description: (
                        <p>
                            {t('RecoveringAccount.LostEmailAccess.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ResetPassword.Title')}`,
                    description: <p>{t('RecoveringAccount.ResetPassword.Content')}</p>
                },
                {
                    title: `${t('RecoveringAccount.ResetPassword.Email.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Email.Content.StepOne'}>
                                    On the
                                    <strong> Sign In </strong>
                                    page, enter your email address.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Email.Content.StepTwo'}>
                                    Click
                                    <strong> Forgot Your Password? </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Email.Content.StepThree'}>
                                    Click
                                    <strong> Reset Your Password </strong>
                                </Trans>
                            </li>
                            <li>{t('RecoveringAccount.ResetPassword.Email.Content.StepFour')}</li>
                            <li>{t('RecoveringAccount.ResetPassword.Email.Content.StepFive')}</li>
                            <li>{t('RecoveringAccount.ResetPassword.Email.Content.StepSix')}</li>
                        </ol>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ResetPassword.Sms.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Sms.Content.StepOne'}>
                                    On the
                                    <strong> Sign In </strong>
                                    page, enter your email address.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Sms.Content.StepTwo'}>
                                    Click
                                    <strong> Forgot Your Password? </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Sms.Content.StepThree'}>
                                    Click
                                    <strong> Reset Your Password </strong>
                                </Trans>
                            </li>
                            <li>{t('RecoveringAccount.ResetPassword.Sms.Content.StepFour')}</li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPassword.Sms.Content.StepFive'}>
                                    Click
                                    <strong> Try With SMS </strong>
                                    <Subtext size='large'>
                                        Note: This option will only be available if you have provided a verified mobile
                                        number
                                    </Subtext>
                                </Trans>
                            </li>
                            <li>{t('RecoveringAccount.ResetPassword.Sms.Content.StepSix')}</li>
                            <li>{t('RecoveringAccount.ResetPassword.Sms.Content.StepSeven')}</li>
                            <li>{t('RecoveringAccount.ResetPassword.Sms.Content.StepEight')}</li>
                        </ol>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ForgotPasswordBroken.Title')}`,
                    description: (
                        <p>
                            {t('RecoveringAccount.ForgotPasswordBroken.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ForgotPasswordCantAccessAuthenticator.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ForgotPasswordCantAccessAuthenticator.StepOne'}>
                                    From the WCL sign-in page, enter your email address and click
                                    <strong> Forgot Your Password?</strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ForgotPasswordCantAccessAuthenticator.StepTwp'}>
                                    On the
                                    <strong> Let's Verify It's You </strong>
                                    page, click
                                    <strong> Recover Account</strong>
                                    .
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ForgotPasswordCantAccessAuthenticator.StepThree'}>
                                    In the
                                    <strong> Recover Account </strong>
                                    pop-up, acknowledge that you want to reset your account.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ForgotPasswordCantAccessAuthenticator.StepFour'}>
                                    Enter the 6-digit, one-time passcode sent to your email and click
                                    <strong> Verify Code</strong>
                                    .
                                </Trans>
                            </li>
                            <li> {t('Enter and confirm a new WCL account password.')} </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ForgotPasswordCantAccessAuthenticator.StepSix'}>
                                    From the <strong> Set Up Authentication Methods </strong>
                                    page, enable an authenticator app for multifactor authentication.
                                </Trans>
                            </li>
                        </ol>
                    )
                },
                {
                    title: `${t('RecoveringAccount.PasswordRecovery.Title')}`,
                    description: (
                        <>
                            <ol>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.PasswordRecovery.StepOne'}>
                                        On your profile page in Workday Central Login, select
                                        <strong> Manage Your Account </strong>
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.PasswordRecovery.StepTwo'}>
                                        From
                                        <strong> Personal Info</strong>
                                        , select
                                        <strong> Contact Information </strong>
                                        .
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.PasswordRecovery.StepThree'}>
                                        To substitute your current primary email with another email to verify and recover your account, click
                                        <strong> Add Email Address </strong>
                                        .
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.PasswordRecovery.StepFour'}>
                                    To add a mobile number for password recovery, select
                                    <strong> Add Mobile Number </strong>
                                    .
                                    </Trans>
                                </li>
                            </ol>
                            <p>{t('RecoveringAccount.PasswordRecovery.Suggestion')}</p></>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ResetPasswordUsingEmail.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPasswordUsingEmail.StepOne'}>
                                    From the sign-in page, enter your email address and click
                                    <strong> Forgot Your Password? </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:RecoveringAccount.ResetPasswordUsingEmail.StepTwp'}>
                                    Click
                                    <strong> Reset Your Password </strong>
                                    .
                                </Trans>
                            </li>
                            <li>{t('RecoveringAccount.ResetPasswordUsingEmail.StepThree')}</li>
                            <li>{t('RecoveringAccount.ResetPasswordUsingEmail.StepFour')}</li>
                        </ol>
                    )
                },
                {
                    title: `${t('RecoveringAccount.ResetPasswordUsingMobile.Title')}`,
                    description: (
                        <>
                            <ol>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.ResetPasswordUsingMobile.StepOne'}>
                                        From the sign-in page, enter your email address and click
                                        <strong> Forgot Your Password? </strong>
                                        .
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.ResetPasswordUsingMobile.StepTwo'}>
                                        Click
                                        <strong> Reset Your Password </strong>
                                        .
                                    </Trans>
                                </li>
                                <li>{t('RecoveringAccount.ResetPasswordUsingMobile.StepThree')}</li>
                                <li>
                                    <Trans i18nKey={'faq:RecoveringAccount.ResetPasswordUsingMobile.StepFour'}>
                                        Click
                                        <strong> Try With SMS </strong>
                                        and enter the 6-digit code sent to your mobile phone.
                                    </Trans>
                                </li>
                            </ol>
                            <div className={styles.infoIcon}>
                                <SystemIcon icon={infoIcon} color={colors.blueberry400} />
                                <span
                                    className={styles.infoText}>{t('RecoveringAccount.ResetPasswordUsingMobile.Disclaimer')}</span>
                            </div>
                        </>
                    )
                },
            ]
        },
        {
            header: `${t('ManageAccount.Header')}`,
            details: [
                {
                    title: `${t('ManageAccount.ChangePassword.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('ManageAccount.ChangePassword.Content.Info')}
                            </p>
                            <ol>
                                <li>
                                    <Trans i18nKey={'faq:ManageAccount.ChangePassword.Content.StepOne'}>
                                        Under your profile, select
                                        <strong> Manage Your Account </strong>.
                                    </Trans>
                                </li>
                                <li>
                                    <Trans i18nKey={'faq:ManageAccount.ChangePassword.Content.StepTwo'}>
                                        Under the
                                        <strong> Security Settings </strong>
                                        tab, click
                                        <strong> Change My Password </strong>
                                        and enter your current password.
                                    </Trans>
                                </li>
                                <li>{t('ManageAccount.ChangePassword.Content.StepThree')}</li>
                                <li>{t('ManageAccount.ChangePassword.Content.StepFour')}</li>
                            </ol>
                        </>
                    )
                },
                {
                    title: `${t('ManageAccount.PasswordRequirements.Title')}`,
                    description: (
                        <>
                            <p>{t('ManageAccount.PasswordRequirements.MustContain.Prelude')}</p>
                            <ul>
                                <li>{t('ManageAccount.PasswordRequirements.MustContain.RequirementOne')}</li>
                                <li>{t('ManageAccount.PasswordRequirements.MustContain.RequirementTwo')}</li>
                                <li>{t('ManageAccount.PasswordRequirements.MustContain.RequirementThree')}</li>
                            </ul>
                            <p>{t('ManageAccount.PasswordRequirements.Forbidden.Prelude')}</p>
                            <ul>
                                <li>{t('ManageAccount.PasswordRequirements.Forbidden.RequirementOne')}</li>
                                <li>
                                    {t('ManageAccount.PasswordRequirements.Forbidden.RequirementTwo')}
                                </li>
                                <li>{t('ManageAccount.PasswordRequirements.Forbidden.RequirementThree')}</li>
                                <li>{t('ManageAccount.PasswordRequirements.Forbidden.RequirementFour')}</li>
                            </ul>
                            <p>{t('ManageAccount.PasswordRequirements.CheckedAgainst.Prelude')}</p>
                            <ul>
                                <li>{t('ManageAccount.PasswordRequirements.CheckedAgainst.RequirementOne')}</li>
                                <li>{t('ManageAccount.PasswordRequirements.CheckedAgainst.RequirementTwo')}</li>
                            </ul>
                            <Subtext size='large'>
                                {t('ManageAccount.PasswordRequirements.CheckedAgainst.AdditionalInfo')}
                            </Subtext>
                        </>
                    )
                },
                {
                    title: `${t('ManageAccount.UpdatePhone.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdatePhone.StepOne'}>
                                    Under your profile, select
                                    <strong> Manage Your Account </strong>.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdatePhone.StepTwo'}>
                                    Under Personal Info, click
                                    <strong> Edit Mobile Number </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdatePhone.StepThree'}>
                                    Enter your
                                    <strong> password </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdatePhone.StepFour'}>
                                    Enter your
                                    <strong> mobile number </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdatePhone.StepFive'}>
                                    Click
                                    <strong> Add your mobile number </strong>
                                </Trans>
                            </li>
                        </ol>
                    )
                },
                {
                    title: `${t('ManageAccount.UpdateEmail.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdateEmail.StepOne'}>
                                    Under your profile, select
                                    <strong> Manage Your Account </strong>
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdateEmail.StepTwo'}>
                                    Under
                                    <strong> Personal Info </strong>
                                    , select
                                    <strong> Contact Information </strong>.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdateEmail.StepTwo'}>
                                    To add one or more email address to your account, click
                                    <strong> + Add Email address </strong>
                                </Trans>
                                <Subtext size='large'>
                                    {t('ManageAccount.UpdateEmail.StepThree.AdditionalInfo')}
                                </Subtext>
                            </li>
                            <li>
                                {t('ManageAccount.UpdateEmail.StepFour')}
                                <Subtext size='large'>
                                    {t('ManageAccount.UpdateEmail.StepFour.AdditionalInfo')}
                                </Subtext>
                            </li>
                        </ol>
                    )
                },
                {
                    title: `${t('ManageAccount.EmailAlias.Title')}`,
                    description: (
                        <p>
                            {t('ManageAccount.EmailAlias.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('ManageAccount.UpdateName.Title')}`,
                    description: (
                        <ol>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdateName.StepOne'}>
                                    Under your profile, select
                                    <strong> Manage Your Account </strong>.
                                </Trans>
                            </li>
                            <li>
                                <Trans i18nKey={'faq:ManageAccount.UpdateName.StepTwo'}>
                                    Under
                                    <strong> Personal Info </strong>
                                    , Click Edit by your
                                    <strong> Profile Name </strong>.
                                </Trans>
                            </li>
                            <li>{t('ManageAccount.UpdateName.StepThree')}</li>
                        </ol>
                    )
                },
                {
                    title: `${t('ManageAccount.EmailPreference.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('ManageAccount.EmailPreference.Content')}
                            </p>
                            <Subtext size='large'>
                                {t('ManageAccount.EmailPreference.Content.AdditionalInfo')}
                            </Subtext>
                        </>
                    )
                },
                {
                    title: `${t('ManageAccount.Session.Title')}`,
                    description: (
                        <p>
                            {t('ManageAccount.Session.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('ManageAccount.ResetAuthenticator.Title')}`,
                    description: (
                        <p>
                            <Trans i18nKey={'faq:ManageAccount.ResetAuthenticator.Content'}>
                                After entering a username and a password, click on
                                <strong> Update Authenticator </strong>.
                                You will receive an email with a 6-digit one-time passcode.
                                After entering the verification code, you can pair a new authenticator app.
                            </Trans>
                        </p>
                    )
                },
                {
                    title: `${t('ManageAccount.NewPhone.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('ManageAccount.NewPhone.Content')}
                            </p>
                            <ol>
                                <li>
                                    <Trans i18nKey={'faq:ManageAccount.NewPhone.Content.StepOne'}>
                                        When WCL prompts you to enter a 6-digit verification code from your authenticator, click
                                        <strong> Reset Authenticator </strong>.
                                        below the prompt.
                                    </Trans>
                                </li>
                                <li>{t('ManageAccount.NewPhone.Content.StepTwo')}</li>
                                <li>{t('ManageAccount.NewPhone.Content.StepThree')}</li>
                            </ol>
                        </>
                    )
                }
            ]
        },
        {
            header: `${t('Security.Header')}`,
            details: [
                {
                    title: `${t('Security.TrustedBrowser.Title')}`,
                    description: (
                        <p>
                            {t('Security.TrustedBrowser.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('Security.ShouldTrustBrowser.Title')}`,
                    description: (
                        <p>
                            {t('Security.ShouldTrustBrowser.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('Security.AccountNotification.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('Security.AccountNotification.Prelude')}
                            </p>
                            <ol>
                                <li>
                                    {t('Security.AccountNotification.StepOne')}
                                </li>
                                <li>
                                    {t('Security.AccountNotification.StepTwo')}
                                </li>
                                <li>
                                    {t('Security.AccountNotification.StepThree')}
                                </li>
                                <li>
                                    {t('Security.AccountNotification.StepFour')}
                                </li>
                            </ol>
                        </>
                    )
                },
                {
                    title: `${t('Security.MissingActivationEmail.Title')}`,
                    description: (
                        <>
                            <p>{t('Security.MissingActivationEmail.Prelude')}</p>
                            <ul>
                                <li>{t('Security.MissingActivationEmail.ReasonOne')}</li>
                                <li>{t('Security.MissingActivationEmail.ReasonTwo')}</li>
                                <li>{t('Security.MissingActivationEmail.ReasonThree')}</li>
                                <li>{t('Security.MissingActivationEmail.ReasonFour')}</li>
                            </ul>
                            <p>
                                <Trans i18nKey={'faq:Security.MissingActivationEmail.AdditionalInfo'}>
                                    You can follow the steps in the
                                    <strong> How do I create an account? </strong>
                                    section above to create an account without having received the activation email.
                                </Trans>
                            </p>
                        </>
                    )
                },
                {
                    title: `${t('Security.AccountLocked.Title')}`,
                    description: (
                        <p>
                            {t('Security.AccountLocked.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('Security.WhyAccountLocked.Title')}`,
                    description: (
                        <>
                            <p>
                                {t('Security.WhyAccountLocked.Content')}
                            </p>
                            <p>
                                {t('Security.WhyAccountLocked.Content.AdditionalInfo')}
                            </p>
                        </>
                    )
                },
                {
                    title: `${t('Security.KeepAccountSafe.Title')}`,
                    description: (
                        <>
                            <ul>
                                <li>{t('Security.KeepAccountSafe.StepOne')}
                                    <ul>
                                        <li>{t('Security.KeepAccountSafe.StepOne.RequirementOne')}</li>
                                        <li>{t('Security.KeepAccountSafe.StepOne.RequirementTwo')}</li>
                                        <li>{t('Security.KeepAccountSafe.StepOne.RequirementThree')}</li>
                                    </ul>
                                </li>
                            </ul>
                            <ul>
                                <li>{t('Security.KeepAccountSafe.StepTwo')}
                                    <ul>
                                        <li>{t('Security.KeepAccountSafe.StepTwo.RequirementOne')}</li>
                                        <li>{t('Security.KeepAccountSafe.StepTwo.RequirementTwo')}</li>
                                        <li>{t('Security.KeepAccountSafe.StepTwo.RequirementThree')}</li>
                                    </ul>
                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <Trans i18nKey={'faq:Security.KeepAccountSafe.StepThree'}>
                                        Monitor any suspicious account activity by checking your email for security notifications or accessing
                                        <strong> Manage Your Account {'>'} Security Settings {'>'} Show logged in sessions. </strong>
                                    </Trans>
                                </li>
                                <li>{t('Security.KeepAccountSafe.StepFour')}</li>
                            </ul>
                            <p>{t('Security.KeepAccountSafe.Suggestion')}</p>
                        </>
                    )
                },
            ]
        },
        {
            header: `${t('OtherIssues.Header')}`,
            details: [
                {
                    title: `${t('OtherIssues.SiteAccess.Title')}`,
                    description: <p>{t('OtherIssues.SiteAccess.Content')}</p>
                },
                {
                    title: `${t('OtherIssues.MissingEmail.Title')}`,
                    description: (
                        <p>
                            <Trans i18nKey={'faq:OtherIssues.MissingEmail.Content'}>
                                If you are experiencing issues receiving password reset or
                                registration emails via Workday Central Login, then check your
                                junk/spam folder. The messages are sent from
                                <strong> no-reply-wcl@workday.com </strong>
                                . Your company IT department may have an email spam filtering
                                system in place. Ensure that emails from Workday Central Login are in the approved
                                list of emails and are in the accept list via your e-mail spam filtering system.
                            </Trans>
                        </p>
                    )
                },
                {
                    title: `${t('OtherIssues.MissingOtp.Title')}`,
                    description: (
                        <p>{t('OtherIssues.MissingOtp.Content')}</p>
                    )
                },
                {
                    title: `${t('OtherIssues.CookieActivity.Title')}`,
                    description: (
                        <p>
                            {t('OtherIssues.CookieActivity.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('OtherIssues.OtpValidDuration.Title')}`,
                    description: (
                        <p>
                            {t('OtherIssues.OtpValidDuration.Content')}
                        </p>
                    )
                },
                {
                    title: `${t('OtherIssues.CantAccessWCL.Title')}`,
                    description: (
                        <>
                            <p> {t('OtherIssues.CantAccessWCL.StepOne')} </p>
                            <p>
                                <Trans i18nKey={'faq:OtherIssues.CantAccessWCL.StepTwo'}>
                                    If you tried clicking the
                                    <strong> Forgot Your Password? </strong>
                                    link on the sign-in page, and it didn't work, clear the cookies and cache of your web browser and try again.
                                </Trans>
                            </p>
                            <div className={styles.infoIcon}>
                                <SystemIcon icon={infoIcon} color={colors.blueberry400} />
                                <span
                                    className={styles.infoText}>{t('OtherIssues.CantAccessWCL.Disclaimer')}
                                </span>
                            </div>
                        </>
                    )
                },
            ]
        }
    ];
    return (

        <UpcSupportPage>
            <div className={styles.content}>
                <h1>{t('PageHeader.Title')}</h1>
                {faqs.map(createFaq)}
            </div>
        </UpcSupportPage>
    );
}
